import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TermsOfServiceModal({ isOpen, onClose }: TermsOfServiceModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-card flex-shrink-0">
          <DialogTitle className="text-lg font-semibold">Termini e Condizioni di Utilizzo</DialogTitle>
        </DialogHeader>
        
        <div className="relative flex-1 bg-background min-h-0">
          <div className="p-6 overflow-y-auto h-full">
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Termini e Condizioni di Utilizzo
                </h1>
                <p className="text-base text-gray-600 dark:text-gray-400 mb-1">Utenti – Gildy.it</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ultimo aggiornamento: 22 Febbraio 2026
                </p>
              </div>

              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1. Titolare della Piattaforma</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Il sito web Gildy.it ("Piattaforma" o "Gildy") è un brand commerciale di proprietà e gestito da:
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed font-semibold">
                    Twibi S.r.l.<br />
                    P.IVA IT15924631003<br />
                    Via Cola Di Rienzo 271, 00192 Roma (RM), Italia<br />
                    PEC: info@pec.twibi.it
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Email: <a href="mailto:info@gildy.it" className="text-gold hover:text-gold/80">info@gildy.it</a>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">2. Accettazione dei Termini</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    L'accesso e l'utilizzo della Piattaforma comportano l'accettazione integrale dei presenti Termini.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Qualora l'utente non intenda accettarli, è tenuto a interrompere l'utilizzo del sito.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">3. Natura del Servizio</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    Gildy è una piattaforma digitale informativa e comparativa che consente agli utenti di:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>consultare quotazioni aggiornate di oro e argento;</li>
                    <li>confrontare operatori compro oro e operatori professionali in oro;</li>
                    <li>utilizzare strumenti di calcolo e simulazione;</li>
                    <li>consultare previsioni generate tramite sistemi algoritmici;</li>
                    <li>bloccare un prezzo presso un operatore;</li>
                    <li>richiedere valutazioni;</li>
                    <li>utilizzare il Vault digitale;</li>
                    <li>lasciare recensioni sugli operatori.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Gildy non acquista né vende metalli preziosi e non è parte delle trattative tra utenti e operatori.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">4. Natura Informativa delle Quotazioni e dei Contenuti</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">Le quotazioni, simulazioni, calcoli e previsioni:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>sono fornite esclusivamente a scopo informativo;</li>
                    <li>non costituiscono offerta contrattuale;</li>
                    <li>non rappresentano consulenza finanziaria o di investimento;</li>
                    <li>non garantiscono valore di realizzo effettivo.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Le previsioni generate tramite sistemi algoritmici hanno carattere statistico e non vincolante.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5. Prezzi Visualizzati nelle Schede Operatori</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">I prezzi visualizzati nelle schede pubbliche:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>sono calcolati automaticamente sulla base dello spread configurato dall'operatore e delle quotazioni di mercato;</li>
                    <li>hanno valore indicativo;</li>
                    <li>possono variare nel tempo.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Il prezzo finale può variare in base alla verifica fisica del bene, al peso effettivo, allo stato di conservazione e alle condizioni applicate dall'Operatore.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Twibi S.r.l. non garantisce che il prezzo visualizzato venga confermato al momento della transazione.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">6. Servizio "Blocca Prezzo"</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Il servizio "Blocca Prezzo" consente all'utente di inviare una richiesta di blocco del prezzo corrente presso un operatore selezionato.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">Il blocco:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>costituisce manifestazione di interesse;</li>
                    <li>non genera obbligo contrattuale per Twibi S.r.l.;</li>
                    <li>può essere accettato, rifiutato o annullato dall'Operatore;</li>
                    <li>non garantisce la conclusione della transazione.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Gildy non garantisce l'effettiva esecuzione del blocco da parte dell'Operatore. Eventuali accordi sono esclusivamente tra utente e Operatore.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">7. Trasmissione dei Dati agli Operatori</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">Quando l'utente invia una richiesta:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>i dati inseriti vengono trasmessi esclusivamente all'Operatore selezionato manualmente;</li>
                    <li>la trasmissione avviene al solo fine di consentire il contatto e la gestione della richiesta.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    La Piattaforma non effettua cessione dei dati a soggetti terzi diversi dall'Operatore selezionato. L'Operatore agisce quale titolare autonomo del trattamento ai sensi del GDPR. Twibi S.r.l. non è responsabile del trattamento successivo effettuato dall'Operatore.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">8. Gildy Trust Score (GTS) e Badge</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Il GTS è un indice algoritmico proprietario utilizzato per organizzare e classificare gli operatori.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">Il GTS e i badge associati:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>hanno funzione informativa;</li>
                    <li>non costituiscono certificazione ufficiale;</li>
                    <li>non rappresentano garanzia di qualità o affidabilità;</li>
                    <li>sono dinamici e soggetti a variazione.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Il GTS non sostituisce la valutazione personale dell'utente.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">9. Vault Digitale</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Il Vault consente l'archiviazione personale di informazioni e documenti.
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>I documenti sono conservati con misure tecniche adeguate e cifratura.</li>
                    <li>I valori visualizzati sono simulazioni matematiche indicative.</li>
                    <li>Non costituiscono perizia ufficiale.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    L'utente può esercitare i diritti previsti dal GDPR.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">10. Recensioni</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Gli utenti possono pubblicare recensioni sugli operatori. L'utente si impegna a:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>fornire informazioni veritiere;</li>
                    <li>non pubblicare contenuti offensivi, diffamatori o illeciti.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Twibi S.r.l. può moderare o rimuovere contenuti non conformi anche senza preventiva comunicazione.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">11. Segnalazioni</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Gli utenti possono segnalare operatori tramite strumenti dedicati. Le segnalazioni devono essere effettuate in buona fede. Twibi S.r.l. può effettuare verifiche e adottare eventuali misure ritenute opportune.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">12. Uso Consentito e Vietato</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">È vietato:</p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>utilizzare la Piattaforma per finalità illecite;</li>
                    <li>effettuare scraping o raccolta sistematica di dati;</li>
                    <li>interferire con il funzionamento tecnico del sito;</li>
                    <li>pubblicare contenuti fraudolenti.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    La violazione può comportare limitazioni o sospensione dell'accesso.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">13. Proprietà Intellettuale</h3>
                  
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-3 mb-2">13.1 Proprietà della Piattaforma</h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Il marchio "Gildy", il logo, il GTS, il software, la struttura dei dati, il design e i contenuti editoriali originali sono di proprietà esclusiva di Twibi S.r.l. È vietata la riproduzione non autorizzata.
                  </p>

                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2">13.2 Contenuti Caricati da Operatori e Utenti</h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    La Piattaforma può ospitare contenuti caricati da operatori o utenti, quali:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>loghi aziendali;</li>
                    <li>fotografie e immagini;</li>
                    <li>descrizioni dell'attività;</li>
                    <li>recensioni e commenti.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Tali contenuti restano di proprietà dei rispettivi titolari. Chi carica contenuti garantisce di disporre dei necessari diritti e autorizzazioni.
                  </p>

                  <h4 className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-2">13.3 Licenza Concessa a Twibi S.r.l.</h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Con il caricamento di contenuti, l'utente o l'operatore concede a Twibi S.r.l. una licenza non esclusiva, gratuita e mondiale per utilizzare, riprodurre, pubblicare e distribuire tali contenuti al fine di:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>consentire il funzionamento della Piattaforma;</li>
                    <li>mostrarli nelle schede pubbliche;</li>
                    <li>permetterne l'indicizzazione;</li>
                    <li>utilizzarli in comunicazioni promozionali legate ai servizi Gildy.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    La proprietà dei contenuti non viene trasferita.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">14. Limitazione di Responsabilità</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Il servizio è fornito "così com'è" e "come disponibile".
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Nei limiti consentiti dalla legge, Twibi S.r.l. non è responsabile per:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
                    <li>decisioni economiche prese dall'utente;</li>
                    <li>perdita di opportunità commerciali;</li>
                    <li>comportamenti degli operatori;</li>
                    <li>mancata conclusione di trattative;</li>
                    <li>variazioni di ranking o punteggi;</li>
                    <li>eventuali differenze tra prezzi visualizzati e condizioni applicate dall'Operatore.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">15. Modifiche ai Termini</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Twibi S.r.l. può modificare i presenti Termini mediante pubblicazione sul sito.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">16. Legge Applicabile e Foro Competente</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    I presenti Termini sono regolati dalla legge italiana.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                    Per ogni controversia è competente il Foro di Roma, salvo il foro del consumatore ove applicabile.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    I consumatori residenti nell'UE possono utilizzare la piattaforma ODR:{' '}
                    <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold/80">
                      https://ec.europa.eu/consumers/odr
                    </a>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">17. Clausola di Salvaguardia</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Qualora una disposizione sia ritenuta invalida o inefficace, le restanti rimarranno pienamente valide.
                  </p>
                </section>

                <section className="border-t pt-4 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contatti</h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Per qualsiasi domanda relativa ai presenti Termini e Condizioni:{' '}
                    <a href="mailto:info@gildy.it" className="text-gold hover:text-gold/80">info@gildy.it</a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-3 border-t bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}